"use client";

import Container from "@mui/material/Container";
import safeStringify from "fast-safe-stringify";

import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import Image from "next/image";

import { useEffect } from "react";

import { ContentfulNavigation } from "../../../../@types/contentfulNavigation";
import {
  IMetadataFields,
  IPageFields,
  IRecipeSection,
} from "../../../../@types/generated/contentful";
import ContentSectionWrapper from "../../../../components/content-section-wrapper";
import SharedHead from "../../../../components/page/head";
import Navigation from "../../../../components/page/navigation";
import { getAllContentfulRecipePages } from "../../../../utils/contentful/getAllContentfulRecipePages";
import getContentfulPage from "../../../../utils/contentful/getContentfulPage";
import { withHttps } from "../../../../utils/helperFunctions";
import generateTitle from "../../../../utils/page/generateTitle";
import getJointPageProperties, {
  JointPageProperties,
} from "../../../../utils/page/getJointPageProperties";
import { DOC_FLECK_TENANT_NAME } from "../../../../utils/tenant-names";
import { isDocFleckTenantPage } from "../../../../utils/tenants";

interface PageProps {
  isPreview?: boolean;
  page: IPageFields;
  previewData?: any;
  navigation: ContentfulNavigation;
  jointPageProperties: JointPageProperties;
}

/**
 * Top level page
 * @param {PageProps} props
 * @param {IPageFields} props.page
 * @returns JSX.Element
 */
const RecipePage: NextPage<PageProps> = ({ page }: PageProps) => {
  const { contentSections, seoMetadata, tenant } = page;
  const {
    fields: { title },
  } = seoMetadata;

  const prepareMetadata = (metadata: IMetadataFields) => {
    const og = metadata.ogMetadata?.fields;
    const twitter = metadata.twitterMetadata?.fields;

    const setRobots = () => {
      if (metadata.noIndex && metadata.noFollow) {
        return "noindex, nofollow";
      }

      if (metadata.noIndex) {
        return "noindex";
      }

      if (metadata.noFollow) {
        return "nofollow";
      }

      return "";
    };

    const robots = setRobots();

    const metatag = {
      title: metadata.title,
      description: metadata.description,
      keywords: metadata.keywords?.join(", "),
      robots,
      ogTitle: og?.title ?? metadata.title,
      ogDescription: og?.description ?? metadata.description,
      ogType: og?.type ?? "",
      ogImage: og?.image ?? twitter?.image ?? "",
      twitterCard: twitter?.card ?? "",
      twitterTitle: twitter?.title ?? metadata.title,
      twitterDescription: twitter?.description ?? metadata.description,
      twitterImage: twitter?.image ?? og?.image ?? "",
    };

    return metatag;
  };

  const metatags = prepareMetadata(seoMetadata.fields);
  const recipeSection = contentSections?.find(
    (section) => section.sys.contentType.sys.id === "recipeSection"
  ) as IRecipeSection;
  const recipeTitle = generateTitle(
    recipeSection.fields.title,
    "Ein Doc Fleck Rezept"
  );

  //   parent.postMessage({ action: "print-recipe-loaded" });
  return (
    <>
      <Head key={recipeTitle}>
        <title>{recipeTitle}</title>
        <meta name="title" content={recipeTitle} />
        <meta name="description" content={metatags.description} />
        {metatags.keywords && (
          <meta name="keywords" content={metatags.keywords} />
        )}
        <meta name="robots" content={metatags.robots} />
        <meta property="og:title" content={metatags.ogTitle} />
        <meta property="og:description" content={metatags.ogDescription} />
        {metatags.ogType && (
          <meta property="og:type" content={metatags.ogType} />
        )}
        {metatags.ogImage && (
          <meta property="og:image" content={metatags.ogImage} />
        )}
        {metatags.twitterCard && (
          <meta property="twitter:card" content={metatags.twitterCard} />
        )}
        <meta property="twitter:title" content={metatags.twitterTitle} />
        <meta
          property="twitter:description"
          content={metatags.twitterDescription}
        />
        {metatags.twitterImage && (
          <meta property="twitter:image" content={metatags.twitterImage} />
        )}
      </Head>
      <SharedHead />

      <Container
        className="page-wrapper"
        maxWidth={false}
        style={{
          minHeight: "100vh",
          display: "flex",
          flexWrap: "nowrap",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <main style={{ flex: "1", overflow: "hidden" }}>
          {contentSections
            ?.filter(
              (section) => section.sys.contentType.sys.id === "recipeSection"
            )
            .map((section, i) => (
              <ContentSectionWrapper
                key={`section-${i}`}
                section={section}
                isPrintView={true}
              ></ContentSectionWrapper>
            ))}
        </main>
      </Container>
    </>
  );
};

export default RecipePage;

/**
 * This function gets called at build time to pre-fetch the routes of the course pages
 * @returns {Promise<{paths: {params: {slug: string}}[], fallback: boolean}>}
 */
export const getStaticPaths: GetStaticPaths = async () => {
  // Call contentful API endpoint to get course pages
  const pages = await getAllContentfulRecipePages();

  // Filter out tenant pages
  const tenantPages = pages.filter((page) => isDocFleckTenantPage(page));
  const recipePages = tenantPages.filter((page) => page.isRecipe);
  // Get the paths we want to pre-render based on the pages
  const paths = recipePages.map((page) => ({
    params: {
      slug: page.slug,
    },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

/**
 * This function gets called at build time on server-side to statically pre-fetch the data for the page
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns page data
 */
export const getStaticProps: GetStaticProps<PageProps> = async (context) => {
  const [page, jointPageProperties] = await Promise.all([
    getContentfulPage({
      // get page slug from context
      slug: context.params?.slug?.toString() || "",
      isPreview: context.preview || false,
      previewData: context.previewData,
    }),
    getJointPageProperties(context, DOC_FLECK_TENANT_NAME),
  ]);

  // if no page was found, return 404
  if (!page) {
    return { notFound: true };
  }

  const propsData = safeStringify({
    page: page.fields,
    theme: DOC_FLECK_TENANT_NAME,
  });

  const props = JSON.parse(propsData);

  // pass page data to the page via props
  return {
    props,
  };
};
